import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "@/runtime-env";
import { appHref, appPathname, IN_SMB } from "@/lib/platform";

const apiClient = axios.create({
	baseURL: API_URL,
});

const AUTH_PATHS = new Set(["/sign-in", ...(IN_SMB ? ["/sign-up"] : []), "/forgot-password", "/reset-password", "/confirm-email"]);

// A hung refresh would otherwise hold the cross-tab lock and stall every tab's requests.
const REFRESH_TIMEOUT_MS = 15_000;

/** `reason` ends up in the sign-in URL, so these logouts show in access logs and user screenshots. */
function forceSignIn(reason: string) {
	localStorage.removeItem("token");

	if (!AUTH_PATHS.has(appPathname())) {
		globalThis.location.href = appHref(`/sign-in?reason=${reason}`);
	}
}

// Web Locks serialises the refresh across tabs. Fallback covers old browsers and non-secure contexts.
const withLock = <T>(fn: () => Promise<T>): Promise<T> =>
	globalThis.navigator?.locks ? navigator.locks.request("auth-refresh", fn) : fn();

/**
 * `token` is the fresh access token, or null when there is none.
 * `rejectedWith` is the HTTP status when /auth/refresh answered with an error; it is absent when the
 * refresh did not get an answer at all (network failure, timeout), which must not end the session.
 */
type RefreshResult = { token: string | null; rejectedWith?: number };

let refreshInFlight: Promise<RefreshResult> | null = null;

// Idempotent on both axes: concurrent callers in one tab share a single promise, and
// concurrent tabs are serialised by the lock, so whoever gets in second finds the fresh
// token already in shared localStorage and never calls /auth/refresh at all. That matters
// because the backend rotates the refresh token: two requests carrying the same one
// invalidate it. Deliberately fetch rather than axios/apiClient, so no interceptor can
// ever attach itself to this request.
function refreshToken(): Promise<RefreshResult> {
	const before = localStorage.getItem("token");

	refreshInFlight ??= withLock(async (): Promise<RefreshResult> => {
		const current = localStorage.getItem("token");
		if (current && current !== before) {
			return { token: current };
		}

		let response: Response;

		try {
			response = await fetch(`${API_URL}/auth/refresh`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: "{}",
				signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
			});
		} catch {
			// Network is down, the request was blocked or timed out. Leave the token alone so the session survives.
			return { token: null };
		}

		if (!response.ok) {
			return { token: null, rejectedWith: response.status };
		}

		const data = await response.json().catch(() => null);

		if (!data?.access_token) {
			// A 200 without a token is a broken response, not a rejection of the session.
			return { token: null };
		}

		localStorage.setItem("token", data.access_token);
		return { token: data.access_token };
	})
		// Anything unexpected inside the lock (e.g. localStorage full) counts as "no answer", so callers
		// still get the original request error instead of this one.
		.catch((): RefreshResult => ({ token: null }))
		.finally(() => {
			refreshInFlight = null;
		});

	return refreshInFlight;
}

apiClient.interceptors.request.use((config) => {
	const token = localStorage.getItem("token");
	config.headers.Authorization = token ? `Bearer ${token}` : "";
	return config;
});

apiClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const status = error.response?.status;

		if (status === 403) {
			setTimeout(() => {
				toast.error("Forbidden");
			}, 0);
		}

		if (status !== 401 || !originalRequest || originalRequest._retry) {
			throw error;
		}

		if (originalRequest.url?.includes("/auth/login") || originalRequest.url?.includes("/auth/verify")) {
			forceSignIn("auth_401");
			throw error;
		}

		originalRequest._retry = true;

		// Another request in this tab, or another tab entirely, may have stored a fresh token
		// while ours was in flight. No refresh needed then, just replay with the current one.
		// Replays go through the request interceptor again, which attaches the token from localStorage.
		const usedToken = originalRequest.headers?.Authorization;
		const current = localStorage.getItem("token");

		if (current && `Bearer ${current}` !== usedToken) {
			return apiClient(originalRequest);
		}

		const { token, rejectedWith } = await refreshToken();

		if (token) {
			return apiClient(originalRequest);
		}

		// Sign out only when the backend rejected the refresh token, which it always does with 401.
		// Anything else (a 5xx during a deploy, a 403 from a proxy or WAF, a timeout) does not mean
		// the session ended, so keep the token and let the next request try again.
		if (rejectedWith === 401) {
			forceSignIn("refresh_401");
		}

		throw error;
	}
);

export default apiClient;
